import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { ActionButtons } from '@/components';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useSurveyDraft } from '@/hooks/residents/corkBoard/useSurveyDraft';
import { SurveyComposer } from '@/components/residents/corkBoard/survey/SurveyComposer';
import { AdminTagChipPicker } from '../AdminTagChipPicker';
import type { CorkBoardTag, CreateSurveyThreadRequest } from '@/types/residents/corkBoard';

type PostKind = 'announcement' | 'survey';

// Pinned to the survey width to avoid a layout jump.
const PANEL_WIDTH = 680;

interface DesktopAdminPostPanelProps {
  visible: boolean;
  kind: PostKind;
  availableTags: CorkBoardTag[];
  isSavingAnnouncement?: boolean;
  isSavingSurvey?: boolean;
  onSaveAnnouncement?: (data: { title: string; body: string; tagIds: number[] }) => void;
  onSaveSurvey?: (data: Omit<CreateSurveyThreadRequest, 'propertyId'>) => void;
  onClose: () => void;
}

export const DesktopAdminPostPanel: React.FC<DesktopAdminPostPanelProps> = memo(
  ({
    visible,
    kind,
    availableTags,
    isSavingAnnouncement = false,
    isSavingSurvey = false,
    onSaveAnnouncement,
    onSaveSurvey,
    onClose,
  }) => {
    const { redesign } = useAdminColors();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [errors, setErrors] = useState<{ title?: string; body?: string; tags?: string }>({});
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [showSurveyErrors, setShowSurveyErrors] = useState(false);

    const draft = useSurveyDraft();
    const {
      reset: resetDraft,
      setEnabled: setDraftEnabled,
      toPayload: draftToPayload,
      isValid: draftIsValid,
      questions: draftQuestions,
      isAnonymous: draftIsAnonymous,
      allowReplies: draftAllowReplies,
      expiresAt: draftExpiresAt,
    } = draft;

    const categoryTags = useMemo(
      () =>
        availableTags
          .filter((t) => t.slug !== 'announcements' && t.slug !== 'survey')
          .map((t) => (t.slug === 'events' ? { ...t, icon: null } : t)),
      [availableTags],
    );

    const surveyTag = useMemo(
      () => availableTags.find((t) => t.slug === 'survey') ?? null,
      [availableTags],
    );

    // Reset all local + draft state whenever the panel becomes visible.
    useEffect(() => {
      if (visible) {
        setTitle('');
        setBody('');
        setSelectedTagIds([]);
        setErrors({});
        setShowDiscardModal(false);
        setShowSurveyErrors(false);
        resetDraft();
        setDraftEnabled(kind === 'survey');
      }
    }, [visible, kind, resetDraft, setDraftEnabled]);

    const handleToggleTag = useCallback((tagId: number) => {
      setSelectedTagIds((prev) => {
        if (prev.includes(tagId)) return prev.filter((id) => id !== tagId);
        return [tagId];
      });
      setErrors((prev) => (prev.tags ? { ...prev, tags: undefined } : prev));
    }, []);

    const validate = useCallback((): boolean => {
      const newErrors: { title?: string; body?: string; tags?: string } = {};
      if (!title.trim()) {
        newErrors.title = 'Title is required';
      } else if (title.trim().length > CORK_BOARD_INPUT_LIMITS.titleMax) {
        newErrors.title = `Title must be ${CORK_BOARD_INPUT_LIMITS.titleMax} characters or fewer`;
      }
      // Body is optional for surveys (the questions are the content).
      if (kind !== 'survey' && !body.trim()) {
        newErrors.body = 'Body is required';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [title, body, kind]);

    const isSaving = kind === 'survey' ? isSavingSurvey : isSavingAnnouncement;
    const canSaveSurvey = kind === 'survey' ? draftIsValid : true;

    const handleSave = useCallback(() => {
      if (isSaving) return;
      if (kind === 'survey') setShowSurveyErrors(true);
      if (!validate() || !canSaveSurvey) return;
      if (kind === 'announcement') {
        onSaveAnnouncement?.({
          title: title.trim(),
          body: body.trim(),
          tagIds: selectedTagIds,
        });
      } else {
        onSaveSurvey?.({
          title: title.trim(),
          body: body.trim(),
          tagIds: selectedTagIds,
          kind: 'survey',
          survey: draftToPayload(),
        });
      }
    }, [
      isSaving,
      validate,
      canSaveSurvey,
      kind,
      onSaveAnnouncement,
      onSaveSurvey,
      title,
      body,
      selectedTagIds,
      draftToPayload,
    ]);

    const handleCancel = useCallback(() => {
      const surveyIsDirty =
        kind === 'survey' &&
        (draftQuestions.length > 0 || draftIsAnonymous || draftAllowReplies || draftExpiresAt !== null);
      const isDirty =
        title.trim() !== '' || body.trim() !== '' || selectedTagIds.length > 0 || surveyIsDirty;
      if (isDirty) {
        setShowDiscardModal(true);
      } else {
        onClose();
      }
    }, [
      title,
      body,
      selectedTagIds,
      kind,
      draftQuestions,
      draftIsAnonymous,
      draftAllowReplies,
      draftExpiresAt,
      onClose,
    ]);

    const panelTitle = kind === 'survey' ? 'New Survey' : 'New Announcement';
    const submitText = kind === 'survey' ? 'Publish Survey' : 'Post Announcement';
    const bodyPlaceholder =
      kind === 'survey' ? 'Optional context shown above the questions...' : 'Write your announcement...';
    const titlePlaceholder = kind === 'survey' ? 'Survey title' : 'Announcement title';

    return (
      <DesktopSidePanel visible={visible} onClose={handleCancel} width={PANEL_WIDTH}>
        <WebConfirmModal
          visible={showDiscardModal}
          title="Discard Changes"
          message="Are you sure you want to discard your changes?"
          confirmText="Discard"
          cancelText="Keep Editing"
          onConfirm={() => {
            setShowDiscardModal(false);
            onClose();
          }}
          onCancel={() => setShowDiscardModal(false)}
        />
        <View style={styles.container}>
          <DesktopPanelHeader title={panelTitle} onClose={handleCancel} />
          <DesktopPanelBody scroll bottomInset={75}>
            <View style={styles.bodyContent}>
              <GlassFormInput
                label="Title"
                required
                value={title}
                onChangeText={(text) => {
                  setTitle(text);
                  if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                placeholder={titlePlaceholder}
                maxLength={CORK_BOARD_INPUT_LIMITS.titleMax}
                error={errors.title}
                autoFocus
              />

              <GlassFormInput
                label="Body"
                required={kind !== 'survey'}
                value={body}
                onChangeText={(text) => {
                  setBody(text);
                  if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
                }}
                placeholder={bodyPlaceholder}
                multiline
                maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
                error={errors.body}
              />

              <View style={styles.field}>
                {kind === 'announcement' && (
                  <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
                    Announcements tag will be added automatically
                  </Text>
                )}
                {kind === 'survey' && (
                  <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
                    Survey tag will be added automatically
                  </Text>
                )}
                <AdminTagChipPicker
                  tags={categoryTags}
                  selectedTagIds={selectedTagIds}
                  onToggleTag={handleToggleTag}
                  lockedTag={kind === 'survey' ? surveyTag : null}
                  label="Category tag (optional)"
                  error={errors.tags}
                />
              </View>

              {kind === 'survey' && (
                <View style={styles.surveySection}>
                  <SurveyComposer draft={draft} showErrors={showSurveyErrors} />
                </View>
              )}
            </View>
          </DesktopPanelBody>
          <View style={[styles.footer, { borderTopColor: redesign.card.border }]}>
            <ActionButtons
              isLoading={isSaving}
              onCancel={handleCancel}
              onSubmit={handleSave}
              submitText={submitText}
              containerStyle={{ backgroundColor: redesign.card.bg }}
            />
          </View>
        </View>
      </DesktopSidePanel>
    );
  },
);

DesktopAdminPostPanel.displayName = 'DesktopAdminPostPanel';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    borderTopWidth: 1,
  },
  bodyContent: {
    paddingHorizontal: 18,
    paddingTop: 8,
    gap: 20,
  },
  field: {
    gap: 6,
  },
  autoTagNote: {
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 4,
  },
  surveySection: {
    marginTop: 4,
  },
});
